import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CInputGroup,
  CNav,
  CNavItem,
  CNavLink,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilGlobeAlt,
  cilLockLocked,
  cilPeople,
  cilPlus,
  cilReload,
  cilSearch,
  cilWarning,
} from '@coreui/icons'
import ToastComponent from 'src/components/common/TaostComponent'
import RowActionsMenu from 'src/components/common/RowActionsMenu'
import LeagueService from 'src/service/LeagueService'
import Notify from '../Notify'
import LeagueForm from './LeagueForm'
import { LEAGUE_TYPE, deleteLeagueMessage, leagueMembersLabel } from './leagueHelpers'
import '../GamePrize/GamePrize.css'

const columns = [
  { key: 'name', label: 'League', sorter: true },
  { key: 'owner_name', label: 'Owner', sorter: true },
  { key: 'members_count', label: 'Members', sorter: true, _style: { width: '10%' } },
  { key: 'is_open', label: 'Joining', sorter: true, _style: { width: '10%' } },
  { key: 'created_at', label: 'Created', sorter: true, _style: { width: '14%' } },
  { key: 'actions', label: 'Actions', sorter: false, _style: { width: '1%' } },
]

const SETTINGS = [
  {
    key: 'is_league_activate',
    label: 'Enable Leagues',
    help: 'Show leagues to members so they can view and join them. Turning it off keeps all leagues.',
  },
  {
    key: 'allow_private_league',
    label: 'Allow Private League Creation',
    help: 'Members can each create one private league in this game.',
  },
]

const Leagues = () => {
  const navigate = useNavigate()
  const [type, setType] = useState(LEAGUE_TYPE.PUBLIC)
  const [leagues, setLeagues] = useState({})
  const [summary, setSummary] = useState({})
  const [setting, setSetting] = useState({})
  const [loading, setLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [columnSorter, setColumnSorter] = useState({})
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [openFilter, setOpenFilter] = useState('')
  const [formVisible, setFormVisible] = useState(false)
  const [editLeague, setEditLeague] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const fetchLeagues = useCallback(async () => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    params.append('type', type)
    if (search) params.append('search', search)
    if (openFilter !== '') params.append('is_open', openFilter)
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }
    try {
      const result = await LeagueService.getLeagues(offset, itemsPerPage, activePage, params)
      setLeagues(result.data)
    } catch (e) {
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [type, activePage, itemsPerPage, columnSorter, search, openFilter])

  const fetchSummary = useCallback(() => {
    LeagueService.getLeagueSummary().then((res) => {
      if (res.success) setSummary(res.data)
    })
  }, [])

  const refresh = useCallback(() => {
    fetchLeagues()
    fetchSummary()
  }, [fetchLeagues, fetchSummary])

  useEffect(() => {
    fetchLeagues()
  }, [fetchLeagues])

  useEffect(() => {
    fetchSummary()
    LeagueService.getLeagueSetting().then((res) => {
      if (res.success) setSetting(res.data || {})
    })
  }, [fetchSummary])

  useEffect(() => {
    const timer = setTimeout(() => {
      const term = searchInput.trim()
      if (search === term) return
      setActivePage(1)
      setSearch(term)
    }, 350)
    return () => clearTimeout(timer)
  }, [searchInput, search])

  const changeTab = (newType) => {
    setType(newType)
    setActivePage(1)
    setColumnSorter({})
  }

  const handleSetting = (key, checked) => {
    setSetting((prev) => ({ ...prev, [key]: checked ? 1 : 0 }))
    LeagueService.saveLeagueSetting({ key, status: checked }).then((res) => {
      if (res.success) {
        setSetting(res.data || {})
        ToastComponent(res.message, 'success')
      } else {
        ToastComponent(res.message, 'error')
      }
    })
  }

  const runAction = (request, onDone) => {
    setActionLoading(true)
    request
      .then((res) => {
        if (res.success) {
          ToastComponent(res.message, 'success')
          onDone?.()
        } else {
          ToastComponent(res.message, 'error')
        }
      })
      .catch(() => ToastComponent('Something went wrong. Please try again.', 'error'))
      .finally(() => setActionLoading(false))
  }

  const toggleJoining = (item) =>
    runAction(
      LeagueService.changeJoiningStatus({ id: item.id, is_open: !item.is_open }),
      fetchLeagues,
    )

  const confirmDelete = () => {
    const target = deleteTarget
    setDeleteTarget(null)
    runAction(LeagueService.deleteLeague({ id: target.id }), refresh)
  }

  const openCreate = () => {
    setEditLeague(null)
    setFormVisible(true)
  }

  const openEdit = (item) => {
    setEditLeague(item)
    setFormVisible(true)
  }

  const leaguesOff = !setting.is_league_activate
  const totalPages = Math.ceil((leagues?.total || 0) / itemsPerPage)
  const isPublic = type === LEAGUE_TYPE.PUBLIC

  const stats = [
    { label: 'Public Leagues', value: summary.public_count, icon: cilGlobeAlt, tone: 'primary' },
    {
      label: 'Private Leagues',
      value: summary.private_count,
      icon: cilLockLocked,
      tone: 'warning',
    },
    {
      label: 'Participating Members',
      value: summary.participant_count,
      icon: cilPeople,
      tone: 'success',
    },
  ]

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div className="ge-page-header mb-0">
          <h1 className="ge-page-title">Leagues</h1>
          <p className="ge-page-subtitle">
            Smaller groups competing within this game, using members&apos; existing teams and
            points.
          </p>
        </div>
        <CButton color="primary" onClick={openCreate}>
          <CIcon icon={cilPlus} className="me-1" />
          Create {isPublic ? 'Public' : 'Private'} League
        </CButton>
      </div>

      <CCard className="mb-3">
        <CCardBody>
          <CRow className="g-3">
            {SETTINGS.map((item) => (
              <CCol md={6} key={item.key}>
                <CFormSwitch
                  id={item.key}
                  label={<strong>{item.label}</strong>}
                  checked={!!setting[item.key]}
                  disabled={item.key !== 'is_league_activate' && leaguesOff}
                  onChange={(e) => handleSetting(item.key, e.target.checked)}
                />
                <div className="small text-medium-emphasis ms-5">{item.help}</div>
              </CCol>
            ))}
          </CRow>
          {leaguesOff && (
            <CAlert color="warning" className="mt-3 mb-0 py-2">
              Leagues are hidden from members. You can still create and manage leagues here.
            </CAlert>
          )}
        </CCardBody>
      </CCard>

      <CRow className="g-3 mb-3">
        {stats.map((stat) => (
          <CCol sm={6} lg={4} key={stat.label}>
            <CCard className="gp-stat-card h-100">
              <CCardBody className="d-flex align-items-center gap-3">
                <span className={`gp-stat-icon gp-stat-icon--${stat.tone}`}>
                  <CIcon icon={stat.icon} size="lg" />
                </span>
                <div>
                  <div className="gp-stat-label">{stat.label}</div>
                  <div className="gp-stat-value">{stat.value ?? 0}</div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        ))}
      </CRow>

      {error && (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load the leagues.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchLeagues}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      )}

      <CCard>
        <CCardBody>
          <CNav variant="tabs" className="mb-3">
            <CNavItem>
              <CNavLink
                role="button"
                active={isPublic}
                onClick={() => changeTab(LEAGUE_TYPE.PUBLIC)}
              >
                Public Leagues
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                role="button"
                active={!isPublic}
                onClick={() => changeTab(LEAGUE_TYPE.PRIVATE)}
              >
                Private Leagues
              </CNavLink>
            </CNavItem>
          </CNav>

          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <CInputGroup className="ge-search">
              <span className="ge-search-icon">
                <CIcon icon={cilSearch} />
              </span>
              <CFormInput
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by league or owner name..."
                aria-label="Search leagues"
              />
            </CInputGroup>
            <CFormSelect
              style={{ maxWidth: 180 }}
              value={openFilter}
              aria-label="Filter by joining status"
              onChange={(e) => {
                setActivePage(1)
                setOpenFilter(e.target.value)
              }}
            >
              <option value="">All statuses</option>
              <option value="1">Open</option>
              <option value="0">Closed</option>
            </CFormSelect>
          </div>

          <div className="ge-datatable ge-datatable--no-select">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              sorterValue={columnSorter}
              noItemsLabel={
                isPublic
                  ? 'No public leagues yet. Use "Create Public League" to add one, e.g. one per club.'
                  : 'No private leagues yet. Use "Create Private League" or wait for members to create one.'
              }
              scopedColumns={{
                name: (item) => (
                  <td>
                    <span
                      className="fw-semibold text-primary"
                      role="button"
                      onClick={() => navigate(`/leagues/${item.id}`)}
                    >
                      {item.name}
                    </span>
                    {item.team_names && (
                      <div className="small text-medium-emphasis">{item.team_names}</div>
                    )}
                    {!isPublic && item.join_code && (
                      <div className="small text-medium-emphasis font-monospace">
                        Code: {item.join_code}
                      </div>
                    )}
                  </td>
                ),
                owner_name: (item) => <td>{item.owner_name || 'Game Admin'}</td>,
                members_count: (item) => <td>{leagueMembersLabel(item)}</td>,
                is_open: (item) => (
                  <td>
                    <CBadge
                      color={item.is_open ? 'success' : 'secondary'}
                      shape="rounded-pill"
                      className="px-2 py-1"
                    >
                      {item.is_open ? 'Open' : 'Closed'}
                    </CBadge>
                  </td>
                ),
                created_at: (item) => <td>{moment(item.created_at).format('DD MMM YYYY')}</td>,
                actions: (item) => (
                  <td>
                    <RowActionsMenu
                      actions={[
                        { label: 'View / Manage', onClick: () => navigate(`/leagues/${item.id}`) },
                        { label: 'Edit', onClick: () => openEdit(item) },
                        {
                          label: item.is_open ? 'Close Joining' : 'Open Joining',
                          disabled: actionLoading,
                          onClick: () => toggleJoining(item),
                        },
                        { label: 'Delete', danger: true, onClick: () => setDeleteTarget(item) },
                      ]}
                    />
                  </td>
                ),
              }}
              items={leagues?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading || actionLoading}
              pagination={{ external: true }}
              paginationProps={{ activePage, pages: totalPages || 1 }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(page) => setActivePage(page)}
              onItemsPerPageChange={(perPage) => {
                setActivePage(1)
                setItemsPerPage(perPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>

      <LeagueForm
        visible={formVisible}
        league={editLeague}
        defaultType={type}
        onClose={() => setFormVisible(false)}
        onSaved={refresh}
      />

      <Notify
        showConfirm={!!deleteTarget}
        handleCancel={() => setDeleteTarget(null)}
        handleConfirm={confirmDelete}
        text={
          deleteTarget ? deleteLeagueMessage(deleteTarget.name, deleteTarget.members_count) : ''
        }
      />
    </>
  )
}

export default Leagues
